// ATG 電子遊戲大廳（lobby）研究用復刻。
//
// 版面、class、scoped-CSS 的 data-v 屬性、文案、行為全部照原版：
//   https://play.godeezone1.com/egames/lobby/game/?t=...&l=zh-tw&socket_url=...&headless=0&dark=0
// 原版是 Vue3 + Pinia + vue-i18n + socket.io 的 SPA，它把 sourcemap 一起發佈了，
// 所以原始 .vue/.ts 全部還原在 reference/lobby/src/，樣式直接沿用原版編譯好的 web/lobby/style.css
// （裡面的 .card[data-v-5c8ff356] 這種 scoped 選擇器就是為什麼下面每個節點都要帶 data-v-*）。
//
// 與原版的差別只有資料來源：原版靠 socket.io 的 lobbyInitial 拿遊戲清單、lobbyPlay 換 redirectUrl；
// 這裡改讀 web/lobby/lobby_initial.json（tools/fetch_lobby.mjs 用真實 token 連 socket 抓回來的原始回應），
// 點「開始遊戲」則走該遊戲的 localUrl（本機有復刻的才有）。

import { IconArcade, IconGoTop, IconLogoBlack, IconLottery, IconMagnifier, IconPoker, IconSlot } from './icons';
import { setLocale, t, locale } from './i18n';
import { LobbySocket } from './socket';

// ---------------------------------------------------------------- 型別（照 src/typings/game.ts、user.ts 的用法還原）

export interface GameType {
  code: string;
  name: string;
  category: string;
  checksum: string;
  isNew: number;
  isHot: number;
  isComingSoon?: number;
  /** 原版 lobbyInitial 就有這欄，但它是 admin 後台網址、公開抓會 403；原版自己也不用（照 checksum 組網址） */
  icon?: string;
  /** 本地化擴充：本機有復刻這款遊戲時的入口網址 */
  localUrl?: string;
}

export interface UserType {
  uid?: string | number | null;
  name?: string | null;
  balance: { currency: string; amount: number };
  avatar?: string | null;
  avatarUrl?: string;
}

interface LobbyContent { games: GameType[]; user: UserType }

// ---------------------------------------------------------------- 常數

/** 原版 bundle 裡的 version 字串（顯示在右下角、#root 的 v 屬性） */
const VERSION = '260721';
/** 頭像 fallback：原版是 CDN，這裡全部本地化（web/lobby/avatars/，由 build.mjs 從 assets/resources 複製） */
const AVATAR_BASE = './avatars';
/**
 * lobby_icon 的來源。原版是 `${PLAY_URL}/egames/<checksum>/game/images/platform/<loc>/lobby_icon.png`，
 * 這裡把 `egames/` 之後的路徑原封不動搬到 web/lobby/ 底下（由 tools/fetch_lobby.mjs 下載），
 * 所以 base 換成相對路徑就好，組網址的公式跟原版一模一樣。改成 PLAY_URL 就會直接連 CDN。
 */
const ICON_BASE = '.';

/** 各元件 scoped CSS 的 hash，對得上 web/lobby/style.css 裡的 [data-v-*] */
const S = {
  app: 'data-v-4e5baccd',
  header: 'data-v-f642c622',
  headerMobile: 'data-v-538d7ec8',
  goTop: 'data-v-7d8d18fe',
  notify: 'data-v-8827f639',
  list: 'data-v-91420a9f',
  card: 'data-v-5c8ff356',
  tab: 'data-v-1376735a',
  search: 'data-v-22793509',
};

const CATEGORIES: { name: string; icon?: () => SVGElement }[] = [
  { name: 'all' },
  { name: 'slot', icon: IconSlot },
  { name: 'poker', icon: IconPoker },
  { name: 'arcade', icon: IconArcade },
  { name: 'lottery', icon: IconLottery },
];

/** 研究版自己的提示（原版沒有這條）：點到本機沒復刻的遊戲時用 Notification 說明 */
const NO_LOCAL_BUILD = '本機沒有這款遊戲的復刻';

// ---------------------------------------------------------------- 狀態

const params = new URLSearchParams(location.search);
setLocale(params.get('l'));

/** 有 socket 就走 socket（server/modules/lobby）；連不上退回靜態 json */
let socket: LobbySocket | null = null;

const state = {
  games: [] as GameType[],
  user: { name: null, balance: { currency: 'TWD', amount: 0 }, avatarUrl: `${AVATAR_BASE}/13.png` } as UserType,
  categories: [] as typeof CATEGORIES,
  tabKey: 'all',
  filteredKey: '',
  errorMsg: '',
  isDark: params.get('dark') === '1',
  hasHead: params.get('headless') !== '1',
};

function filtered(): GameType[] {
  const key = state.filteredKey.toLowerCase();
  if (state.tabKey === 'all' && !key) return state.games;
  if (state.tabKey === 'all') return state.games.filter((g) => g.name.toLowerCase().includes(key));
  return state.games.filter((g) => g.category === state.tabKey && g.name.toLowerCase().includes(key));
}

// ---------------------------------------------------------------- DOM 小工具

type Child = Node | string | null | undefined | false;

function h(tag: string, attrs: Record<string, unknown> = {}, ...children: Child[]): HTMLElement {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'onclick') el.addEventListener('click', v as EventListener);
    else if (k === 'oninput') el.addEventListener('input', v as EventListener);
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

/** v-show */
function show(el: HTMLElement, on: boolean) {
  el.style.display = on ? '' : 'none';
}

/** Vue <Transition name="x"> 的 class 流程（enter-from/active → leave-active/to） */
/**
 * Vue <Transition> 的手工版。
 *
 * 下一拍要用 rAF（讓瀏覽器先套上 enter-from 再移除，才會有過場），但**分頁不在前景時 Chrome 會停掉 rAF**，
 * 那樣元素會卡在 enter-from（opacity 0）＝ 東西在畫面上卻看不見。所以 rAF 與 setTimeout 兩條都排，
 * 誰先到誰做，用 done 旗標保證只做一次。
 */
function transition(el: HTMLElement, name: string, on: boolean, ms = 300) {
  el.classList.remove(`${name}-enter-from`, `${name}-enter-active`, `${name}-leave-active`, `${name}-leave-to`);
  const nextTick = (fn: () => void) => {
    let done = false;
    const once = () => { if (done) return; done = true; fn(); };
    requestAnimationFrame(() => requestAnimationFrame(once));
    setTimeout(once, 50);
  };
  if (on) {
    el.style.display = '';
    el.classList.add(`${name}-enter-from`, `${name}-enter-active`);
    nextTick(() => {
      el.classList.remove(`${name}-enter-from`);
      setTimeout(() => el.classList.remove(`${name}-enter-active`), ms);
    });
  } else {
    el.classList.add(`${name}-leave-active`);
    nextTick(() => {
      el.classList.add(`${name}-leave-to`);
      setTimeout(() => {
        el.style.display = 'none';
        el.classList.remove(`${name}-leave-active`, `${name}-leave-to`);
      }, ms);
    });
  }
}

/** @vueuse/core useThrottleFn */
function throttle(fn: () => void, ms: number) {
  let last = 0;
  return () => {
    const now = Date.now();
    if (now - last < ms) return;
    last = now;
    fn();
  };
}

// ---------------------------------------------------------------- Notification（src/components/common/Notification.vue）

const notify = (() => {
  let timer = 0;
  const content = h('div', { class: 'content', [S.notify]: true });
  const title = h('div', { [S.notify]: true });
  const wrap = h('div', { class: 'wrap', [S.notify]: true }, title, content);
  wrap.style.display = 'none';
  const desktop = window.matchMedia('(min-width: 769px)');
  const sync = () => wrap.classList.toggle('isDesktop', desktop.matches);
  desktop.addEventListener('change', sync);
  sync();
  wrap.addEventListener('click', () => close());
  document.body.appendChild(wrap);

  function close() { transition(wrap, desktop.matches ? 'popup' : 'popTop', false); }
  function open(_title: string, _content = '', duration = 6000) {
    title.textContent = _title;
    content.textContent = _content;
    show(content, !!_content);
    transition(wrap, desktop.matches ? 'popup' : 'popTop', true);
    if (duration !== 0) {
      clearTimeout(timer);
      timer = window.setTimeout(close, duration);
    }
  }
  return { open, close };
})();

// ---------------------------------------------------------------- Header（電腦版）

function Header(): HTMLElement {
  const logoLight = h('img', { src: 'img/logo.png', class: 'h-[76px]' });
  const logoDark = h('img', { src: 'img/atg-logo2.png', class: 'h-[76px]' });
  show(logoLight, !state.isDark);
  show(logoDark, state.isDark);
  return h('div', { id: 'header', class: 'hidden md:block', [S.header]: true, [S.app]: true },
    h('div', { class: 'root-container', [S.header]: true },
      h('a', { href: `./${location.search}`, class: 'logo mr-auto', [S.header]: true }, logoLight, logoDark),
      h('div', { class: 'btn mx-[20px]', [S.header]: true },
        h('img', { src: 'img/user.svg', class: 'h-[28px] 2lg:h-[34px]' }),
        h('div', { class: 'w-full text-center' }, state.user.name ?? ''),
      ),
      h('div', { class: 'btn', [S.header]: true },
        h('img', { src: 'img/bag.svg', class: 'h-[28px] 2lg:h-[34px]' }),
        h('div', { class: 'w-full text-center' }, state.user.balance.amount.toLocaleString()),
      ),
    ),
  );
}

// ---------------------------------------------------------------- HeaderMobile（手機版）

function HeaderMobile(): HTMLElement {
  const logoLight = h('img', { src: 'img/logo.png', class: 'h-[50px]' });
  const logoDark = h('img', { src: 'img/atg-logo2.png', class: 'h-[50px]' });
  show(logoLight, !state.isDark);
  show(logoDark, state.isDark);

  const bars = [h('span', { [S.headerMobile]: true }), h('span', { [S.headerMobile]: true }), h('span', { [S.headerMobile]: true })];
  const wrap = h('div', { class: 'wrap', [S.headerMobile]: true }, ...bars);
  const panelWrap = h('div', { class: 'panel-wrap', [S.headerMobile]: true },
    h('div', { class: 'panel', [S.headerMobile]: true },
      h('div', { class: 'btn !pl-5', [S.headerMobile]: true },
        h('img', { src: 'img/user.svg', class: 'h-[34px]' }),
        h('div', { class: 'w-full text-center' }, state.user.name ?? ''),
      ),
      h('div', { class: 'btn', [S.headerMobile]: true },
        h('img', { src: 'img/bag.svg', class: 'h-[36px]' }),
        h('div', { class: 'w-full text-center' }, state.user.balance.amount.toLocaleString()),
      ),
    ),
  );

  let isOpen = false;
  const hamburger = h('div', {
    class: 'hamburger', [S.headerMobile]: true,
    onclick: () => { isOpen = !isOpen; wrap.classList.toggle('active', isOpen); panelWrap.classList.toggle('active', isOpen); },
  }, wrap);

  return h('div', { id: 'header', class: 'block md:hidden', [S.headerMobile]: true, [S.app]: true },
    h('div', { class: 'root-container', [S.headerMobile]: true },
      h('div', { class: 'flex w-full items-center', [S.headerMobile]: true },
        h('a', { href: `./${location.search}`, class: 'logo mr-auto', [S.headerMobile]: true }, logoLight, logoDark),
        hamburger,
      ),
      panelWrap,
    ),
  );
}

// ---------------------------------------------------------------- Search

function Search(cls: string): HTMLElement {
  const input = h('input', {
    type: 'text', placeholder: t('遊戲搜尋'), [S.search]: true,
    oninput: (e: Event) => {
      state.filteredKey = (e.target as HTMLInputElement).value;
      searchInputs.forEach((i) => { if (i !== e.target) i.value = state.filteredKey; });
      renderList();
    },
  }) as HTMLInputElement;
  searchInputs.push(input);
  const icon = IconMagnifier();
  icon.setAttribute('class', 'magnifier');
  icon.setAttribute(S.search, '');
  return h('div', { id: 'Search', class: cls, [S.search]: true },
    h('div', { class: 'bar', [S.search]: true }, input, icon),
  );
}
const searchInputs: HTMLInputElement[] = [];

// ---------------------------------------------------------------- Tab

function Tabs(): HTMLElement[] {
  return state.categories.map((cat) => {
    const btn = h('button', {
      class: `tab flex items-center justify-center gap-2${state.isDark ? ' dark' : ''}${cat.name === state.tabKey ? ' active' : ''}`,
      [S.tab]: true,
      onclick: () => {
        state.tabKey = cat.name;
        tabButtons.forEach((b, i) => b.classList.toggle('active', state.categories[i].name === state.tabKey));
        renderList();
      },
    });
    if (cat.icon) {
      const icon = cat.icon();
      icon.setAttribute('class', 'hidden w-7 2lg:block');
      icon.setAttribute(S.tab, '');
      btn.appendChild(icon);
    }
    btn.appendChild(h('span', { [S.tab]: true }, cat.name !== 'all' ? `ATG ${t(`categories.${cat.name}`)}` : t(`categories.${cat.name}`)));
    tabButtons.push(btn);
    return btn;
  });
}
const tabButtons: HTMLElement[] = [];

// ---------------------------------------------------------------- LazyImg（skeleton → 原語系圖 → en 圖 → 佔位）

function LazyImg(src: string, enSrc: string, alt: string): HTMLElement {
  const holder = h('div', { class: 'skeleton aspect-[262/223] w-full', [S.card]: true });
  const swap = (el: HTMLElement) => holder.replaceWith(el);

  const tryLoad = (url: string, onFail: () => void) => {
    const probe = new Image();
    probe.onload = () => swap(h('img', { src: url, class: 'h-full w-full object-cover', alt, [S.card]: true }));
    probe.onerror = onFail;
    probe.src = url;
  };
  tryLoad(src, () => tryLoad(enSrc, () => {
    const logo = IconLogoBlack();
    logo.setAttribute('class', 'max-h-[40%] max-w-[50%]');
    logo.setAttribute(S.card, '');
    swap(h('div', { class: 'flex aspect-[262/223] w-full flex-col items-center justify-center bg-[#292929]', [S.card]: true },
      logo,
      h('span', { class: 'px-2 pt-3 text-center text-sm leading-4 tracking-widest opacity-30 md:py-3 md:text-xl', [S.card]: true }, alt),
    ));
  }));
  return holder;
}

// ---------------------------------------------------------------- GameCard

function iconUrl(game: GameType, loc: string): string {
  return `${ICON_BASE}/egames/${game.checksum}/game/images/platform/${loc}/lobby_icon.png`;
}

function GameCard(game: GameType): HTMLElement {
  const isEn = locale === 'en' || locale === 'vn';
  const disable = !!game.isComingSoon;
  const headless = params.get('headless') === '1';
  const onClick = throttle(() => { void handlePlay(game); }, headless ? 1000 : 5000);

  const hot = h('div', { class: 'tag', [S.card]: true },
    h('img', { src: 'img/label_icon_red.png', [S.card]: true }), h('span', { [S.card]: true }, t('熱門')));
  const neu = h('div', { class: 'tag', [S.card]: true },
    h('img', { src: 'img/label_icon_blue.png', [S.card]: true }), h('span', { [S.card]: true }, t('最新')));
  show(hot, !game.isNew && !!game.isHot);
  show(neu, !!game.isNew);

  return h('div', { class: `card${isEn ? ' isEn' : ''}${disable ? ' isDisable' : ''}`, [S.card]: true, [S.list]: true },
    h('div', { class: 'img-wrap', [S.card]: true }, LazyImg(iconUrl(game, locale || 'en'), iconUrl(game, 'en'), game.name)),
    h('div', { class: 'mask', [S.card]: true },
      h('button', { class: 'btn', disabled: disable, [S.card]: true, onclick: onClick },
        h('div', { class: 'z-[1] -translate-y-[1px]', [S.card]: true }, disable ? t('comingSoon') : t('開始遊戲')),
      ),
    ),
    hot, neu,
  );
}

/** 原版是 socket 的 lobbyPlay 回 redirectUrl；這裡用遊戲自己的 localUrl */
async function handlePlay(game: GameType) {
  const headless = params.get('headless') === '1';
  const mobileUi = params.get('mobile_ui') === '1';

  // 有 socket 就跟原版一樣走 lobbyPlay 拿 redirectUrl（裡面已經帶好 token 與 goback_url）
  if (socket) {
    try {
      let url = await socket.play(game.code);
      if (mobileUi) url += (url.includes('?') ? '&' : '?') + 'mobile_ui=1';
      if (headless) { window.open(url, '_blank'); return; }
      location.href = url;
      return;
    } catch (e) {
      console.error('[lobby] 進遊戲失敗', e);   // 伺服器 / 連線的錯誤細節只進 console
      notify.open(game.name, '遊戲暫時無法進入，請稍後再試');
      return;
    }
  }

  if (!game.localUrl) { notify.open(game.name, NO_LOCAL_BUILD); return; }
  let url = game.localUrl + (mobileUi ? (game.localUrl.includes('?') ? '&' : '?') + 'mobile_ui=1' : '');
  if (headless) { window.open(url, '_blank'); return; }
  url += (url.includes('?') ? '&' : '?') + 'goback_url=' + encodeURIComponent(location.href);
  location.href = url;
}

// ---------------------------------------------------------------- GameList / PageHome / App

const listRoot = h('div', { [S.list]: true });

function renderList() {
  const games = filtered();
  listRoot.textContent = '';
  if (games.length !== 0) {
    listRoot.appendChild(h('div', { id: 'gameList', [S.list]: true }, ...games.map(GameCard)));
  } else {
    listRoot.appendChild(h('div', { class: 'grid h-[80vh] place-content-center text-[#5c5c5c]', [S.list]: true }, t('noData')));
  }
}

function PageHome(): HTMLElement {
  const tabRow = h('div', { class: 'flex w-full items-center justify-center gap-1 md:gap-2 2lg:gap-4', [S.app]: true });
  if (state.categories.length > 2) tabRow.append(...Tabs());
  tabRow.appendChild(Search('hidden w-full md:block'));
  return h('div', { class: 'root-container', [S.app]: true },
    h('div', { class: 'relative my-10 flex flex-col gap-4', [S.app]: true }, Search('flex w-full md:hidden'), tabRow),
    listRoot,
  );
}

function mount() {
  document.documentElement.setAttribute('class', state.isDark ? 'dark' : 'light');

  const errMask = h('div', { class: 'mask bg-[#292929] text-white', [S.app]: true },
    h('span', { class: 'p-3 text-center text-xl tracking-widest opacity-60', [S.app]: true }, state.errorMsg));
  show(errMask, !!state.errorMsg);

  const goTopIcon = IconGoTop();
  goTopIcon.setAttribute(S.goTop, '');
  goTopIcon.addEventListener('click', () => window.scrollTo({ behavior: 'smooth', top: 0 }));
  const goTop = h('div', { id: 'goTop', class: 'flex items-center justify-center', [S.goTop]: true, [S.app]: true }, goTopIcon);
  goTop.style.display = 'none';

  const root = h('div', { id: 'root', v: VERSION, class: state.hasHead ? 'hasHead' : '', [S.app]: true });
  const header = Header();
  const headerMobile = HeaderMobile();
  show(header, state.hasHead);
  show(headerMobile, state.hasHead);
  root.append(header, headerMobile, PageHome(), goTop, errMask,
    h('div', { id: 'Notification', [S.notify]: true, [S.app]: true }),
    h('div', { class: 'fixed bottom-0 right-0 z-20 scale-75 text-xs', [S.app]: true }, VERSION));

  document.getElementById('app')!.appendChild(root);
  renderList();

  let shown = false;
  window.addEventListener('scroll', () => {
    const on = state.hasHead && window.scrollY > 50;
    if (on === shown) return;
    shown = on;
    transition(goTop, 'fade', on);
  }, { passive: true });
}

// ---------------------------------------------------------------- 進場：讀 lobby_initial.json（= 原版 lobbyInitial 的 content）

async function boot() {
  // 1. 先試 socket（server/modules/lobby）。原版大廳就是這樣拿遊戲清單與玩家資料的
  try {
    const s = new LobbySocket(params);
    const content = await s.initial();
    socket = s;
    socket.onAlert((msg) => notify.open('系統公告', msg));
    state.games = [...(content.games ?? [])];
    if (content.user) state.user = content.user;
    finishBoot();
    return;
  } catch (e) {
    console.warn('[lobby] 連不上大廳 socket，改用靜態 json：', e);
  }

  // 2. 沒有 server 時的離線 fallback：lobby_initial.json + local_games.json
  try {
    const [content, local] = await Promise.all([
      fetch('lobby_initial.json', { cache: 'no-store' }).then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json() as Promise<LobbyContent>;
      }),
      fetch('local_games.json', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({})) as Promise<Record<string, string>>,
    ]);
    state.games = [...(content.games ?? [])];
    state.games.forEach((g) => { if (local[g.code]) g.localUrl = local[g.code]; });
    if (content.user) state.user = content.user;
  } catch (e) {
    // 正式環境（非 demo）伺服器不給離線清單（見 server/app.ts）：大廳只能用平台發的 token 登入
    state.errorMsg = '請由平台重新進入遊戲';
    console.error('[lobby]', e);
  }
  finishBoot();
}

function finishBoot() {
  const used: string[] = [];
  state.games.forEach((g) => { if (!used.includes(g.category)) used.push(g.category); });
  state.categories = CATEGORIES.filter((c) => c.name === 'all' || used.includes(c.name));
  mount();
}

boot();
